import { Link, useLocation } from "react-router-dom"

import { useI18n } from "@/shared/i18n"
import { useFeatureFlag } from "@/shared/features"
import { telegram } from "@/shared/lib/telegram"

import { cn } from "@/shared/lib/utils"

import InventoryIcon from "@/assets/icons/inventory-icon.svg?react"
import SwordsIcon from "@/assets/icons/swords.svg?react"
import ShieldIcon from "@/assets/icons/shield.svg?react"
import GiftIcon from "@/assets/icons/gift.svg?react"
import ProfileIcon from "@/assets/icons/profile.svg?react"

import navigationBgUrl from "@/assets/textures/navigation-bg.svg?url"
import { horizontalNineSliceStyle } from "@/shared/lib/horizontalNineSlice"

/** Native nav chrome 374×60 — fixed jagged end caps. */
const NAV_BG_STYLE = horizontalNineSliceStyle({
  url: navigationBgUrl,
  sliceLeft: 36,
  sliceRight: 37,
  capWidthLeft: 36,
  capWidthRight: 37,
})

const SOON_BADGE_CLASS =
  "absolute left-1/2 top-[17px] z-10 -translate-x-1/2 rounded-full bg-[#3B2F7A] px-1 text-[8px] font-bold leading-[10px] tracking-[-0.5px] text-white"

const NEW_BADGE_CLASS =
  "absolute left-1/2 top-[17px] z-10 -translate-x-1/2 rounded-full bg-[#F25035] px-1 text-[8px] font-bold leading-[10px] tracking-[-0.5px] text-white"

const DISABLED_TAB_CLASS =
  "pointer-events-none flex shrink-0 flex-col items-center gap-0.5 text-white/20 transition-all"

export const BottomNav = () => {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const giftsEnabled = useFeatureFlag("gifts")
  const tournamentsEnabled = useFeatureFlag("tournaments")

  const handleNavClick = () => {
    telegram.hapticSelection()
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex flex-col bg-transparent px-2">
      {/*
        Top gap was pt-1.5 (~6px). Keep it visually but **transparent**: solid nav bg lived on
        `<nav>` and painted over the betting bar glow (betting stays z-40). Spacer passes pointer
        through; chrome + safe-area inset stay opaque on the inner block below.
       */}
      <div
        className="pointer-events-none h-1.5 shrink-0 select-none"
        aria-hidden
      />
      <div className="bg-primary-main pb-[calc(var(--app-safe-bottom)+8px)]">
        <ul className="relative mx-auto flex h-[60px] w-full max-w-2xl items-center justify-between gap-2 px-8 py-3">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={NAV_BG_STYLE}
          />

          <li className="relative">
            {!giftsEnabled && (
              <span className={SOON_BADGE_CLASS}>{t("bottomNav.soonBadge")}</span>
            )}
            {giftsEnabled ? (
              <Link
                to="/inventory"
                onClick={handleNavClick}
                className={cn(
                  "flex shrink-0 flex-col items-center gap-0.5 transition-all",
                  pathname === "/inventory"
                    ? "text-[#00FE74]"
                    : "text-white/40 hover:text-white/80"
                )}
              >
                <InventoryIcon className="size-6" />
                <span className="text-xs tracking-[-0.5px]">
                  {t("bottomNav.inventory")}
                </span>
              </Link>
            ) : (
              <span aria-disabled className={DISABLED_TAB_CLASS}>
                <InventoryIcon className="size-6" />
                <span className="text-xs tracking-[-0.5px]">
                  {t("bottomNav.inventory")}
                </span>
              </span>
            )}
          </li>
          <li className="relative">
            <Link
              to="/"
              className={cn(
                "flex shrink-0 flex-col items-center gap-0.5 transition-all",
                pathname === "/"
                  ? "text-[#00FE74]"
                  : "text-white/40 hover:text-white/80"
              )}
            >
              <SwordsIcon className="size-6" />
              <span className="text-xs tracking-[-0.5px]">
                {t("bottomNav.pvp")}
              </span>
            </Link>
          </li>
          <li className="relative">
            <span className={SOON_BADGE_CLASS}>{t("bottomNav.soonBadge")}</span>
            <Link to="#" className={DISABLED_TAB_CLASS}>
              <ShieldIcon className="size-6" />
              <span className="text-xs tracking-[-0.5px]">
                {t("bottomNav.solo")}
              </span>
            </Link>
          </li>
          <li className="relative">
            {tournamentsEnabled && (
              <span className={NEW_BADGE_CLASS}>{t("bottomNav.newBadge")}</span>
            )}
            <Link
              to="/rewards"
              className={cn(
                "flex shrink-0 flex-col items-center gap-0.5 transition-all",
                pathname === "/rewards"
                  ? "text-[#00FE74]"
                  : "text-white/40 hover:text-white/80"
              )}
            >
              <GiftIcon className="size-6" />
              <span className="text-xs tracking-[-0.5px]">
                {t("bottomNav.rewards")}
              </span>
            </Link>
          </li>
          <li className="relative">
            <Link
              to="/profile"
              className={cn(
                "flex shrink-0 flex-col items-center gap-0.5 transition-all",
                pathname === "/profile"
                  ? "text-[#00FE74]"
                  : "text-white/40 hover:text-white/80"
              )}
            >
              <ProfileIcon className="size-6" />
              <span className="text-xs tracking-[-0.5px]">
                {t("bottomNav.profile")}
              </span>
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  )
}
